import { useState, useRef, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { LocalDocumentItem, isPendingDocument } from './JobDocuments';

const DESCRIPTION_MAX_LENGTH = 85;

interface DraggableDocumentItemProps {
  document: LocalDocumentItem;
  documents: LocalDocumentItem[];
  onDelete: (id: number | string) => void;
  onDescriptionChange: (id: number | string, newDescription: string) => void;
  onRefNumberChange: (id: number | string, newRefNumber: string) => void;
  onDocumentTypeChange: (id: number | string, newType: string, paperColor?: string) => void;
  isDragDisabled: boolean;
  editingField: string | null;
  onEditingFieldChange: (fieldKey: string | null) => void;
}

const DraggableDocumentItem = ({
  document,
  documents,
  onDelete,
  onDescriptionChange,
  onRefNumberChange,
  onDocumentTypeChange,
  isDragDisabled,
  editingField,
  onEditingFieldChange,
}: DraggableDocumentItemProps) => {
  const itemId = isPendingDocument(document) ? document.tempId : document.id;
  const descFieldKey = `description-${itemId}`;
  const refFieldKey = `ref-${itemId}`;
  const isEditingDesc = editingField === descFieldKey;
  const isEditingRef = editingField === refFieldKey;

  const description = isPendingDocument(document) ? (document.description || '') : (document.docketDescription || '');
  const refNumber = isPendingDocument(document) ? (document.refNumber || '') : (document.docketReferenceNumber || '');

  const [localDesc, setLocalDesc] = useState(description);
  const [localRef, setLocalRef] = useState(refNumber);
  const [descError, setDescError] = useState<string | null>(null);
  const descInputRef = useRef<HTMLTextAreaElement>(null);
  const refInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setLocalDesc(description); }, [description]);
  useEffect(() => { setLocalRef(refNumber); }, [refNumber]);

  // Focus + cursor positioning for description
  useEffect(() => {
    if (isEditingDesc && descInputRef.current) {
      descInputRef.current.focus();
      const len = descInputRef.current.value.length;
      descInputRef.current.setSelectionRange(len, len);
    }
  }, [isEditingDesc]);

  // Focus + cursor positioning for ref number
  useEffect(() => {
    if (isEditingRef && refInputRef.current) {
      refInputRef.current.focus();
      const len = refInputRef.current.value.length;
      refInputRef.current.setSelectionRange(len, len);
    }
  }, [isEditingRef]);

  // Auto-resize description textarea
  useEffect(() => {
    if (isEditingDesc && descInputRef.current) {
      descInputRef.current.style.height = 'auto';
      descInputRef.current.style.height = `${descInputRef.current.scrollHeight}px`;
    }
  }, [localDesc, isEditingDesc]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: itemId, disabled: isDragDisabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(
      transform ? { ...transform, x: 0, scaleX: 1, scaleY: 1 } : null
    ),
    transition,
    opacity: isDragging ? 0.5 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: isDragging ? '#f5f5f5' : '#fff',
  };

  const cellStyle: React.CSSProperties = {
    flex: 1,
    fontSize: '0.875rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const filename = isPendingDocument(document) ? document.filename : document.storedFile.filename;
  const pageCount = isPendingDocument(document) ? '—' : document.pageCount;
  const documentType = isPendingDocument(document) ? (document.documentType || '') : (document.type || '');
  const paperColor = isPendingDocument(document) ? (document.paperColor || '') : (document.paperColor || '');

  // Check if ballot/proof_of_claim already exists in other documents
  const hasBallot = documents.some((doc) => {
    const id = isPendingDocument(doc) ? doc.tempId : doc.id;
    if (id === itemId) return false;
    const type = isPendingDocument(doc) ? doc.documentType : doc.type;
    return type === 'ballot';
  });
  const hasProofOfClaim = documents.some((doc) => {
    const id = isPendingDocument(doc) ? doc.tempId : doc.id;
    if (id === itemId) return false;
    const type = isPendingDocument(doc) ? doc.documentType : doc.type;
    return type === 'proof_of_claim';
  });

  // --- Description handlers ---
  const commitDesc = () => {
    const trimmed = localDesc.trim();
    if (trimmed.length > DESCRIPTION_MAX_LENGTH) {
      setDescError(`Max ${DESCRIPTION_MAX_LENGTH} characters`);
      return;
    }
    setDescError(null);
    onDescriptionChange(itemId, trimmed);
    onEditingFieldChange(null);
  };

  const handleDescKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      descInputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setLocalDesc(description);
      setDescError(null);
      onEditingFieldChange(null);
    }
  };

  const handleDescBlur = () => { commitDesc(); };

  // --- Ref number handlers ---
  const commitRef = () => {
    onRefNumberChange(itemId, localRef.trim());
    onEditingFieldChange(null);
  };

  const handleRefKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      refInputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setLocalRef(refNumber);
      onEditingFieldChange(null);
    }
  };

  const handleRefBlur = () => { commitRef(); };

  // --- Shared inline input style ---
  const inlineInputStyle: React.CSSProperties = {
    width: '100%',
    fontSize: '0.875rem',
    padding: '0.375rem 0.5rem',
    border: '1px solid #436EB2',
    borderRadius: '3px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    lineHeight: '1.4',
  };

  const readOnlyStyle = (hasValue: boolean, isRequired: boolean): React.CSSProperties => ({
    cursor: 'pointer',
    padding: '0.25rem 0.5rem',
    borderRadius: '3px',
    border: (!hasValue && isRequired) ? '1px solid #d32f2f' : '1px solid #bdbdbd',
    display: 'block',
    width: '100%',
    minHeight: '1.5em',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  });

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {!isDragDisabled && (
        <span
          {...listeners}
          style={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            color: '#757575',
            backgroundColor: '#F4F4F4',
            borderRadius: '4px',
            padding: '4px',
          }}
          aria-label="Drag to reorder"
        >
          <DragIndicatorIcon fontSize="small" />
        </span>
      )}

      {/* File name */}
      <span style={{ ...cellStyle, flex: 1.5, wordBreak: 'break-word', whiteSpace: 'normal' }}>
        {filename}
        {isPendingDocument(document) && (
          <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#757575', fontStyle: 'italic' }}>
            (pending upload)
          </span>
        )}
      </span>

      {/* Description */}
      <span style={{ ...cellStyle, flex: 1.5, overflow: 'visible', whiteSpace: 'normal' }}>
        {isEditingDesc ? (
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <textarea
              ref={descInputRef}
              value={localDesc}
              placeholder="Enter description"
              onChange={(e) => {
                const val = e.target.value;
                if (val.length <= DESCRIPTION_MAX_LENGTH) {
                  setLocalDesc(val);
                  setDescError(null);
                }
              }}
              onBlur={handleDescBlur}
              onKeyDown={handleDescKeyDown}
              maxLength={DESCRIPTION_MAX_LENGTH}
              rows={1}
              style={{
                ...inlineInputStyle,
                border: descError ? '1px solid #d32f2f' : '1px solid #436EB2',
                resize: 'none',
                overflow: 'hidden',
              }}
              aria-label="Edit document description"
            />
            <span style={{ fontSize: '0.7rem', color: !localDesc.trim() ? '#d32f2f' : '#757575', marginTop: '2px' }}>
              {!localDesc.trim() ? 'Required' : `${localDesc.length}/${DESCRIPTION_MAX_LENGTH}`}
            </span>
            {descError && (
              <span style={{ fontSize: '0.7rem', color: '#d32f2f', marginTop: '2px' }}>
                {descError}
              </span>
            )}
          </div>
        ) : (
          <span
            onClick={() => onEditingFieldChange(descFieldKey)}
            style={readOnlyStyle(!!description, true)}
            title="Click to edit description"
          >
            {description || <span style={{ color: '#d32f2f', fontStyle: 'italic' }}>Enter description</span>}
          </span>
        )}
      </span>

      {/* ECF Docket Ref. No. */}
      <span style={{ ...cellStyle, overflow: 'hidden', whiteSpace: 'nowrap', flex: 0.7 }}>
        {isEditingRef ? (
          <input
            ref={refInputRef}
            type="text"
            value={localRef}
            placeholder="0"
            onChange={(e) => setLocalRef(e.target.value)}
            onBlur={handleRefBlur}
            onKeyDown={handleRefKeyDown}
            style={{ ...inlineInputStyle, width: '100%', maxWidth: '60px', height: '28px', textAlign: 'center' }}
            aria-label="Edit ECF Docket Reference Number"
          />
        ) : (
          <span
            onClick={() => onEditingFieldChange(refFieldKey)}
            style={{ ...readOnlyStyle(!!refNumber, false), width: '100%', maxWidth: '60px', height: '28px', textAlign: 'center', minHeight: 'unset', overflowX: 'auto', overflowY: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', wordBreak: 'normal', scrollbarWidth: 'none' }}
            title="Click to edit ECF Docket Ref. No."
          >
            {refNumber || <span style={{ color: '#757575' }}>0</span>}
          </span>
        )}
      </span>

      {/* Document Type selector */}
      <span style={{ ...cellStyle, flex: 1.2, overflow: 'visible' }}>
        <select
          value={documentType || 'default'}
          onChange={(e) => {
            const newType = e.target.value;
            const newPaperColor = newType === 'ballot' ? 'standard' : undefined;
            onDocumentTypeChange(itemId, newType, newPaperColor);
          }}
          style={{
            fontSize: '0.8rem',
            padding: '0.25rem 0.375rem',
            border: '1px solid #bdbdbd',
            borderRadius: '3px',
            outline: 'none',
            cursor: 'pointer',
            backgroundColor: '#fff',
          }}
          aria-label="Document type"
        >
          <option value="default">Standard</option>
          <option value="ballot" disabled={hasBallot}>Ballot</option>
          <option value="proof_of_claim" disabled={hasProofOfClaim}>Proof of Claim</option>
        </select>
      </span>

      {/* Paper Color selector (only for Ballot) */}
      <span style={{ ...cellStyle, flex: 1, overflow: 'visible' }}>
        {documentType === 'ballot' ? (
          <select
            value={paperColor || 'standard'}
            onChange={(e) => {
              onDocumentTypeChange(itemId, documentType, e.target.value);
            }}
            style={{
              fontSize: '0.8rem',
              padding: '0.25rem 0.375rem',
              border: '1px solid #bdbdbd',
              borderRadius: '3px',
              outline: 'none',
              cursor: 'pointer',
              backgroundColor: '#fff',
            }}
            aria-label="Paper color"
          >
            <option value="standard">Standard</option>
            <option value="color">Colored</option>
          </select>
        ) : (
          <span style={{
            fontSize: '0.8rem',
            color: 'rgba(29, 27, 32, 0.50)',
            backgroundColor: 'rgba(217, 218, 221, 0.40)',
            borderRadius: '4.2px',
            padding: '0.25rem 0.5rem',
          }}>Standard</span>
        )}
      </span>

      {/* Pages */}
      <span style={{ ...cellStyle, flex: 0.5, textAlign: 'center' }}>
        {pageCount}
      </span>

      {/* Delete */}
      <button
        onClick={() => onDelete(itemId)}
        style={{
          display: 'flex',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0.25rem',
          color: '#d32f2f',
        }}
        aria-label={`Delete ${filename}`}
      >
        <DeleteOutlineIcon fontSize="small" />
      </button>
    </div>
  );
};

export default DraggableDocumentItem;
